<template>
  <v-dialog v-model="dialog" max-width="1100" :fullscreen="$vuetify.breakpoint.mobile">
    <v-card
      :class="{'d-flex flex-column': $vuetify.breakpoint.mobile}"
      :style="$vuetify.breakpoint.mobile ? 'height:100%' : ''"
    >
      <v-card-title>
        <v-icon class="mr-2">mdi-greenhouse</v-icon>
        {{ fork_context ? 'Serre nel fork stagionale' : 'Gestione Serre' }}
      </v-card-title>
      <v-card-text class="pa-0" :class="{'flex-grow-1 overflow-y-auto': $vuetify.breakpoint.mobile}">
        <v-row no-gutters style="min-height: 500px;">
          <!-- Colonna sinistra: form -->
          <v-col cols="12" md="7" class="pa-6">
            <div class="d-flex align-center mb-4">
              <span class="text-subtitle-1 font-weight-medium">
                {{ serra_in_modifica ? (fork_context ? 'Modifica nel fork' : 'Modifica Serra') : (fork_context && !nuova_in_fork ? 'Seleziona una serra dalla lista' : 'Nuova Serra') }}
              </span>
              <v-spacer />
              <v-btn v-if="(serra_in_modifica || nuova_in_fork) && !proprieta_readonly" text small color="grey" @click="annullaModifica">
                <v-icon small class="mr-1">mdi-close</v-icon>Annulla
              </v-btn>
            </div>

            <!-- In fork mode senza selezione: solo placeholder.
                 La creazione è disabilitata: creerebbe una Serra live permanente,
                 visibile nella mappa aziendale e in tutti gli altri fork. -->
            <template v-if="fork_context && !serra_in_modifica && !nuova_in_fork">
              <v-alert type="info" outlined dense class="mb-3">
                Clicca su una serra nella lista per modificarne nome, tipo e dimensioni nel fork di questa stagione.
              </v-alert>
              <v-alert type="info" outlined dense class="mb-0" style="font-size:13px">
                Per creare una serra nuova usa la <strong>Mappa Aziendale</strong>, poi aggiungila
                al layout di questa stagione dal canvas.
              </v-alert>
            </template>

            <!-- ===== Form proprietà serra ===== -->
            <v-form ref="form" v-model="form_valid" v-if="(!fork_context || serra_in_modifica || nuova_in_fork) && !proprieta_readonly">
              <v-text-field
                v-model="serra_form.nome"
                label="Nome"
                outlined
                dense
                :readonly="proprieta_readonly"
                :rules="[(v) => !!v || 'Campo obbligatorio']"
              />
              <v-select
                v-model="serra_form.tipo"
                :items="tipi_serra"
                label="Tipo struttura"
                outlined
                dense
                clearable
                :readonly="proprieta_readonly"
                :disabled="proprieta_readonly"
                class="mb-1"
              >
                <template #item="{ item }">
                  <div class="d-flex align-center py-1">
                    <div :style="{
                      width: '12px', height: '12px', borderRadius: '2px',
                      backgroundColor: item.fill,
                      border: `2px solid ${item.stroke}`,
                      marginRight: '10px', flexShrink: 0,
                    }"/>
                    {{ item.text }}
                  </div>
                </template>
                <template #selection="{ item }">
                  <div class="d-flex align-center">
                    <div :style="{
                      width: '10px', height: '10px', borderRadius: '2px',
                      backgroundColor: item.fill,
                      border: `2px solid ${item.stroke}`,
                      marginRight: '8px', flexShrink: 0,
                    }"/>
                    {{ item.text }}
                  </div>
                </template>
              </v-select>
              <v-row dense>
                <v-col cols="4">
                  <v-text-field
                    v-model.number="serra_form.posizione"
                    label="Posizione"
                    type="number"
                    outlined
                    dense
                    :min="1"
                    :readonly="proprieta_readonly"
                    hint="Ordine in lista"
                    persistent-hint
                  />
                </v-col>
                <v-col cols="4">
                  <v-text-field
                    v-model.number="serra_form.larghezza"
                    label="Larghezza (m)"
                    type="number"
                    outlined
                    dense
                    :readonly="proprieta_readonly"
                    :rules="[nonNegativeRule]"
                  />
                </v-col>
                <v-col cols="4">
                  <v-text-field
                    v-model.number="serra_form.lunghezza"
                    label="Lunghezza (m)"
                    type="number"
                    outlined
                    dense
                    :readonly="proprieta_readonly"
                    :rules="[nonNegativeRule]"
                  />
                </v-col>
              </v-row>

              <v-alert v-if="save_error" type="error" outlined dense class="mt-2">
                {{ save_error }}
              </v-alert>
            </v-form>

            <div v-if="(!fork_context || serra_in_modifica || nuova_in_fork) && !proprieta_readonly" class="d-flex justify-end mt-2">
              <v-btn text @click="closeDialog" class="mr-2">Annulla</v-btn>
              <v-btn
                v-if="!proprieta_readonly"
                color="primary"
                :loading="loading_save"
                :disabled="!form_valid"
                @click="salva"
              >
                {{ fork_context ? 'Aggiorna fork' : (serra_in_modifica ? 'Aggiorna' : 'Salva') }}
              </v-btn>
            </div>

            <!-- ===== Sezione FilaStagione (solo con serra selezionata) ===== -->
            <template v-if="serra_in_modifica">
              <v-divider v-if="!proprieta_readonly" class="my-5" />

              <!-- Nessuna stagione -->
              <v-alert v-if="!stagione_attiva" type="info" outlined dense>
                Seleziona una stagione per gestire le file.
              </v-alert>

              <template v-else>
                <!-- Titolo sezione file -->
                <div class="d-flex align-center mb-3">
                  <v-icon small class="mr-1">mdi-view-sequential</v-icon>
                  <span class="text-body-2 font-weight-medium">
                    File / Bancali — stagione {{ stagione_attiva.anno }}
                  </span>
                  <v-spacer />
                  <!-- Copia da stagione precedente -->
                  <v-menu offset-y>
                    <template #activator="{ on }">
                      <v-btn x-small outlined color="blue-grey" v-on="on">
                        <v-icon x-small class="mr-1">mdi-content-copy</v-icon>
                        Copia da…
                      </v-btn>
                    </template>
                    <v-list dense>
                      <v-list-item
                        v-for="st in stagioni_altre"
                        :key="st.id"
                        @click="copiaFileDaStagione(st)"
                      >
                        <v-list-item-title>
                          {{ st.tipo_stagione ? st.tipo_stagione.nome : '' }} {{ st.anno }}
                        </v-list-item-title>
                      </v-list-item>
                      <v-list-item v-if="!stagioni_altre.length" disabled>
                        <v-list-item-title class="grey--text">Nessuna altra stagione</v-list-item-title>
                      </v-list-item>
                    </v-list>
                  </v-menu>
                </div>

                <!-- Quick generate -->
                <div class="d-flex align-center mb-3" style="gap:8px">
                  <v-text-field
                    v-model.number="genera_num_file"
                    label="N. file"
                    type="number"
                    outlined
                    dense
                    hide-details
                    style="max-width:90px"
                    :min="1"
                  />
                  <v-text-field
                    v-model.number="genera_num_vasi"
                    label="Vasi/fila"
                    type="number"
                    outlined
                    dense
                    hide-details
                    style="max-width:90px"
                    :min="1"
                  />
                  <v-btn small outlined color="blue-grey" @click="generaFile">
                    <v-icon small class="mr-1">mdi-auto-fix</v-icon>Genera
                  </v-btn>
                </div>

                <!-- Tabella file draft -->
                <v-progress-linear v-if="loading_file_stagione" indeterminate class="mb-2" />
                <div v-else-if="file_draft.length" class="file-table mb-2">
                  <v-row dense class="caption grey--text mb-1" align="center">
                    <v-col cols="1">N°</v-col>
                    <v-col cols="4">Vasi</v-col>
                    <v-col cols="5" class="text-center">Corridoio dopo</v-col>
                    <v-col cols="2"></v-col>
                  </v-row>
                  <v-row
                    v-for="(fila, idx) in file_draft"
                    :key="fila._key"
                    dense
                    align="center"
                    class="mb-1"
                  >
                    <v-col cols="1" class="caption grey--text">{{ idx + 1 }}</v-col>
                    <v-col cols="4">
                      <v-text-field
                        v-model.number="fila.num_vasi"
                        type="number"
                        outlined
                        dense
                        hide-details
                        :min="1"
                      />
                    </v-col>
                    <v-col cols="5" class="d-flex justify-center">
                      <v-checkbox
                        v-model="fila.corridoio_dopo"
                        dense
                        hide-details
                        class="mt-0 pt-0"
                        color="brown lighten-1"
                      />
                    </v-col>
                    <v-col cols="2" class="d-flex justify-end">
                      <v-btn icon x-small color="red" @click="rimuoviFila(idx)">
                        <v-icon x-small>mdi-minus-circle</v-icon>
                      </v-btn>
                    </v-col>
                  </v-row>
                </div>
                <v-alert v-else dense outlined type="info" class="mb-2">
                  Nessuna fila per questa stagione. Usa "Genera" o aggiungi manualmente.
                </v-alert>

                <div class="d-flex align-center mb-3" style="gap:8px">
                  <v-btn small outlined color="primary" @click="aggiungiFila">
                    <v-icon small class="mr-1">mdi-plus</v-icon>Aggiungi fila
                  </v-btn>
                  <v-spacer />
                  <v-alert v-if="save_file_error" type="error" outlined dense class="mb-0 py-1">
                    {{ save_file_error }}
                  </v-alert>
                  <v-btn
                    small
                    color="teal darken-1"
                    dark
                    :loading="loading_save_file"
                    @click="salvaFileStagione"
                  >
                    <v-icon small class="mr-1">mdi-content-save</v-icon>
                    Salva file stagione
                  </v-btn>
                </div>
              </template>
            </template>
          </v-col>

          <template v-if="!solo_form">
          <v-divider vertical />

          <!-- Colonna destra: lista serre -->
          <v-col cols="12" md="5" class="pa-6 lista-col">
            <div class="d-flex align-center text-subtitle-1 font-weight-medium mb-3">
              <v-icon class="mr-2">mdi-greenhouse</v-icon>
              {{ fork_context ? 'Serre nel fork' : 'Serre disponibili' }}
              <v-spacer />
              <v-switch
                v-if="!fork_context"
                v-model="show_dismesse"
                dense
                hide-details
                class="mt-0 shrink"
                style="font-size:12px"
                label="Dismesse"
              />
            </div>
            <v-text-field
              v-model="filtro_serre"
              placeholder="Cerca serra..."
              outlined
              dense
              hide-details
              clearable
              prepend-inner-icon="mdi-magnify"
              class="mb-3"
            />
            <v-alert v-if="delete_error" :type="delete_dati_collegati ? 'warning' : 'error'" outlined dense class="mb-3">
              {{ delete_error }}
              <div v-if="delete_dati_collegati" class="mt-2 d-flex flex-wrap align-center" style="gap:6px">
                <v-chip v-if="delete_dati_collegati.file_stagione" x-small label color="orange darken-1" dark>
                  {{ delete_dati_collegati.file_stagione }} file stagione
                </v-chip>
                <v-chip v-if="delete_dati_collegati.piazzamenti" x-small label color="orange darken-1" dark>
                  {{ delete_dati_collegati.piazzamenti }} piazzamenti
                </v-chip>
                <v-chip v-if="delete_dati_collegati.appezzamenti" x-small label color="orange darken-1" dark>
                  {{ delete_dati_collegati.appezzamenti }} appezzamenti
                </v-chip>
                <v-chip v-if="delete_dati_collegati.fork_stagionali" x-small label color="orange darken-1" dark>
                  {{ delete_dati_collegati.fork_stagionali }} fork stagionali
                </v-chip>
                <v-btn x-small outlined color="warning" class="ml-auto" @click="dismetteSerra(delete_target_id)">
                  <v-icon x-small class="mr-1">mdi-archive-arrow-down</v-icon>
                  Dismetti
                </v-btn>
              </div>
            </v-alert>
            <v-progress-linear v-if="loading_serre" indeterminate class="mb-4" />
            <v-alert v-else-if="!serre.length" type="info" outlined dense>
              Nessuna serra configurata.
            </v-alert>
            <v-alert v-else-if="!serre_filtrate.length" type="info" outlined dense>
              Nessuna serra corrisponde alla ricerca.
            </v-alert>
            <v-list v-else dense class="py-0">
              <v-list-item v-for="serra in serre_filtrate" :key="serra.id" class="px-0 mb-1">
                <v-list-item-avatar size="36" rounded :style="{
                  backgroundColor: serra.dismessa ? '#ECEFF1' : tipoFill(serra.tipo),
                  border: `2px solid ${serra.dismessa ? '#B0BEC5' : tipoStroke(serra.tipo)}`,
                  minWidth: '36px',
                }">
                </v-list-item-avatar>
                <v-list-item-content>
                  <v-list-item-title class="font-weight-medium" :class="serra.dismessa ? 'grey--text' : ''">
                    {{ serra.nome }}
                    <v-chip v-if="serra.dismessa" x-small label color="grey" dark class="ml-1">Dismessa</v-chip>
                  </v-list-item-title>
                  <v-list-item-subtitle>
                    <span v-if="serra.larghezza || serra.lunghezza">
                      {{ serra.larghezza || '?' }}×{{ serra.lunghezza || '?' }} m
                    </span>
                    <span v-if="stagione_attiva && !fork_context">
                      — {{ fileCountPerStagione(serra.id) }} file
                    </span>
                  </v-list-item-subtitle>
                </v-list-item-content>
                <v-list-item-action class="flex-row align-center ma-0" style="gap:0">
                  <template v-if="serra.dismessa">
                    <v-btn icon small color="green darken-1" title="Riattiva serra" @click="riattivaSerra(serra.id)">
                      <v-icon small>mdi-restore</v-icon>
                    </v-btn>
                  </template>
                  <template v-else>
                    <v-btn icon small color="primary" @click="apriModifica(serra)">
                      <v-icon small>mdi-pencil</v-icon>
                    </v-btn>
                    <v-btn
                      v-if="!fork_context"
                      icon
                      small
                      color="grey darken-1"
                      title="Dismetti serra"
                      @click="dismetteSerra(serra.id)"
                    >
                      <v-icon small>mdi-archive-arrow-down</v-icon>
                    </v-btn>
                    <v-btn
                      icon
                      small
                      color="red"
                      :loading="deleting_id === serra.id"
                      :disabled="!!deleting_id"
                      :title="fork_context ? 'Rimuovi dal fork' : 'Elimina serra'"
                      @click="eliminaSerra(serra)"
                    >
                      <v-icon small>{{ fork_context ? 'mdi-link-off' : 'mdi-delete' }}</v-icon>
                    </v-btn>
                  </template>
                </v-list-item-action>
              </v-list-item>
            </v-list>
          </v-col>
          </template>
        </v-row>
      </v-card-text>
      <v-card-actions v-if="proprieta_readonly">
        <v-spacer />
        <v-btn text @click="closeDialog">Chiudi</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
let _key_counter = 0;
function newKey() {
  return "fila-" + ++_key_counter;
}

export default {
  name: "GestioneSerreDialog",
  props: {
    solo_form: { type: Boolean, default: false },
    stagione_attiva: { type: Object, default: null },
    // Quando presente: la dialog opera sul fork della stagione (non sulla Serra live).
    // { id, id_stagione, id_appezzamento, serre: SerraFork[] }
    fork_context: { type: Object, default: null },
  },
  data() {
    return {
      dialog: false,
      nuova_in_fork: false,
      show_dismesse: false,
      form_valid: false,
      loading_save: false,
      loading_serre: false,
      loading_file_stagione: false,
      loading_save_file: false,
      deleting_id: null,
      save_error: "",
      save_file_error: "",
      delete_error: "",
      delete_dati_collegati: null,
      delete_target_id: null,
      serra_in_modifica: null,
      serra_form: { nome: "", tipo: null, posizione: null, larghezza: null, lunghezza: null },
      tipi_serra: [
        { value: 'ferrovetro',   text: 'Ferrovetro',        fill: '#E3F2FD', stroke: '#1565C0' },
        { value: 'film',         text: 'Film',              fill: '#FFF8E1', stroke: '#E65100' },
        { value: 'ombreggiante', text: 'Solo ombreggiante', fill: '#EDE7F6', stroke: '#4527A0' },
        { value: 'campo_aperto', text: 'Campo aperto',      fill: '#E8F5E9', stroke: '#2E7D32' },
      ],
      file_draft: [],
      genera_num_file: 10,
      genera_num_vasi: 60,
      filtro_serre: "",
    };
  },
  watch: {
    dialog(val) {
      if (!val) this._cleanBackState();
    },
  },
  computed: {
    // Quando c'è una stagione ma nessun fork_context (es. mobile), le proprietà fisiche
    // della Serra live non devono essere modificabili: andrebbero al fork o alla mappa aziendale.
    proprieta_readonly() {
      return !!this.stagione_attiva && !this.fork_context;
    },
    serre() {
      if (this.fork_context) {
        return (this.fork_context.serre || []).map((s) => ({
          id: String(s.id_serra_ref),
          nome: s.nome,
          tipo: s.tipo,
          larghezza: s.larghezza,
          lunghezza: s.lunghezza,
          posizione: null,
          _fork_pos_id: String(s.id),
        }));
      }
      return (this.$store.getters.getSerre || []).filter(s => this.show_dismesse || !s.dismessa);
    },
    serre_filtrate() {
      const q = (this.filtro_serre || "").trim().toLowerCase();
      if (!q) return this.serre;
      return this.serre.filter((s) => s.nome.toLowerCase().includes(q));
    },
    stagioni_altre() {
      const tutte = this.$store.getters.getStagioniColtivazione || [];
      if (!this.stagione_attiva) return tutte;
      return tutte.filter((s) => s.id !== this.stagione_attiva.id);
    },
  },
  methods: {
    openDialog() {
      this.dialog = true;
      this.delete_error = "";
      this.delete_dati_collegati = null;
      this.delete_target_id = null;
      this.resetForm();
      if (!this.fork_context) this.fetchSerre();
      this._pushBackState();
    },
    openDialogConSerra(id_serra) {
      this.dialog = true;
      this.delete_error = "";
      this.delete_dati_collegati = null;
      this.delete_target_id = null;
      this.resetForm();
      if (this.fork_context) {
        this.$nextTick(() => {
          const entry = this.serre.find((s) => s.id === String(id_serra));
          if (entry) this.apriModifica(entry);
        });
      } else {
        this.fetchSerre().then(() => {
          const serra = this.serre.find((s) => s.id === id_serra);
          if (serra) this.apriModifica(serra);
        });
      }
      this._pushBackState();
    },
    closeDialog() {
      this.dialog = false;
      this.resetForm();
    },
    fetchSerre() {
      this.loading_serre = true;
      return this.$store.dispatch("fetchSerre").finally(() => {
        this.loading_serre = false;
      });
    },
    generaFile() {
      const n = Number(this.genera_num_file);
      const m = Number(this.genera_num_vasi);
      if (!n || n < 1 || !m || m < 1) return;
      this.file_draft = Array.from({ length: n }, (_, i) => ({
        _key: newKey(),
        id: null,
        numero: i + 1,
        num_vasi: m,
        corridoio_dopo: false,
      }));
    },
    aggiungiFila() {
      this.file_draft.push({
        _key: newKey(),
        id: null,
        numero: this.file_draft.length + 1,
        num_vasi: this.genera_num_vasi || 100,
        corridoio_dopo: false,
      });
    },
    rimuoviFila(idx) {
      this.file_draft.splice(idx, 1);
    },
    apriModifica(serra) {
      this.serra_in_modifica = serra;
      this.save_error = "";
      this.save_file_error = "";
      this.serra_form = {
        nome:      serra.nome      || "",
        tipo:      serra.tipo      || null,
        posizione: serra.posizione ?? null,
        larghezza: serra.larghezza ?? null,
        lunghezza: serra.lunghezza ?? null,
      };
      this.file_draft = [];
      this.$nextTick(() => {
        if (this.$refs.form) this.$refs.form.resetValidation();
      });
      // Carica FilaStagione se c'è una stagione attiva
      if (this.stagione_attiva) {
        this._caricaFileStagione(serra.id);
      }
    },
    _caricaFileStagione(id_serra) {
      this.loading_file_stagione = true;
      this.$store
        .dispatch('fetchFileStagione', {
          id_serra,
          id_stagione: this.stagione_attiva.id,
        })
        .then((file) => {
          this.file_draft = file.map((f) => ({
            _key: "fs-" + f.id,
            id: f.id,
            numero: f.numero,
            num_vasi: f.num_vasi,
            corridoio_dopo: !!f.corridoio_dopo,
          }));
        })
        .finally(() => {
          this.loading_file_stagione = false;
        });
    },
    async copiaFileDaStagione(stagione_sorgente) {
      if (!this.serra_in_modifica || !this.stagione_attiva) return;
      this.loading_file_stagione = true;
      try {
        const file = await this.$store.dispatch('fetchFileStagione', {
          id_serra: this.serra_in_modifica.id,
          id_stagione: stagione_sorgente.id,
        });
        if (!file.length) {
          this.save_file_error = "La stagione selezionata non ha file configurate per questa serra.";
          return;
        }
        // Copia come nuovi (senza id) così il salvataggio crea record nuovi
        this.file_draft = file.map((f, i) => ({
          _key: newKey(),
          id: null,
          numero: i + 1,
          num_vasi: f.num_vasi,
          corridoio_dopo: !!f.corridoio_dopo,
        }));
        this.save_file_error = "";
      } catch {
        this.save_file_error = "Errore nel caricamento della stagione sorgente.";
      } finally {
        this.loading_file_stagione = false;
      }
    },
    annullaModifica() {
      this.serra_in_modifica = null;
      this.nuova_in_fork     = false;
      this.save_error        = "";
      this.save_file_error   = "";
      this.resetForm();
    },
    buildSerraPayload() {
      return {
        nome:      this.serra_form.nome,
        tipo:      this.serra_form.tipo      || null,
        posizione: this.serra_form.posizione || null,
        larghezza: this.serra_form.larghezza || null,
        lunghezza: this.serra_form.lunghezza || null,
      };
    },
    salva() {
      if (!this.$refs.form.validate()) return;
      this.loading_save = true;
      this.save_error = "";
      const payload = this.buildSerraPayload();

      let action;
      if (this.fork_context && this.serra_in_modifica) {
        // Modalità fork: aggiorna le proprietà snapshot nel fork (non la Serra live)
        action = this.$store.dispatch("updateSerraFork", {
          id_layout: this.fork_context.id,
          id_pos: this.serra_in_modifica._fork_pos_id,
          id_stagione: this.fork_context.id_stagione,
          id_appezzamento: this.fork_context.id_appezzamento,
          payload,
        }).then(() => this.annullaModifica());
      } else if (this.fork_context && this.nuova_in_fork) {
        // Nuova serra creata direttamente nel fork (non va nel live)
        action = this.$store.dispatch("saveSerraInFork", {
          id_layout:        this.fork_context.id,
          id_stagione:      this.fork_context.id_stagione,
          id_appezzamento:  this.fork_context.id_appezzamento,
          payload: { ...payload, x: 0, y: 0, rotazione: 0 },
        }).then((serra) => {
          this.nuova_in_fork     = false;
          this.serra_in_modifica = serra;
          this.serra_form = {
            nome:      serra.nome      || "",
            tipo:      serra.tipo      || null,
            posizione: serra.posizione ?? null,
            larghezza: serra.larghezza ?? null,
            lunghezza: serra.lunghezza ?? null,
          };
        });
      } else {
        const isNew = !this.serra_in_modifica;
        action = (isNew
          ? this.$store.dispatch("saveSerra", payload)
          : this.$store.dispatch("updateSerra", { id: this.serra_in_modifica.id, payload })
        ).then((serra) => {
          if (isNew) {
            this.serra_in_modifica = serra;
            this.serra_form = {
              nome:      serra.nome      || "",
              tipo:      serra.tipo      || null,
              posizione: serra.posizione ?? null,
              larghezza: serra.larghezza ?? null,
              lunghezza: serra.lunghezza ?? null,
            };
          }
        });
      }

      action
        .then(() => this.$emit("serre-aggiornate"))
        .catch(() => { this.save_error = "Errore durante il salvataggio. Riprova."; })
        .finally(() => { this.loading_save = false; });
    },
    async salvaFileStagione() {
      if (!this.serra_in_modifica || !this.stagione_attiva) return;
      this.loading_save_file = true;
      this.save_file_error = "";
      const file = this.file_draft.map((f, i) => ({
        ...(f.id ? { id: f.id } : {}),
        numero: i + 1,
        num_vasi: Number(f.num_vasi),
        corridoio_dopo: !!f.corridoio_dopo,
      }));
      try {
        const salvate = await this.$store.dispatch('saveFileStagione', {
          id_serra: this.serra_in_modifica.id,
          id_stagione: this.stagione_attiva.id,
          file,
        });
        // Aggiorna il draft con gli id assegnati dal backend
        this.file_draft = salvate.map((f) => ({
          _key: "fs-" + f.id,
          id: f.id,
          numero: f.numero,
          num_vasi: f.num_vasi,
          corridoio_dopo: !!f.corridoio_dopo,
        }));
        this.$emit("serre-aggiornate");
      } catch {
        this.save_file_error = "Errore durante il salvataggio delle file.";
      } finally {
        this.loading_save_file = false;
      }
    },
    dismetteSerra(id) {
      // Opera sulla Serra live: mai raggiungibile dal fork stagionale
      if (this.fork_context) return;
      const serra = (this.$store.getters.getSerre || []).find(s => String(s.id) === String(id));
      if (!serra) return;
      this.$store.dispatch('updateSerra', { id, payload: { ...serra, dismessa: true } })
        .then(() => {
          this.delete_error = '';
          this.delete_dati_collegati = null;
          this.delete_target_id = null;
          if (this.serra_in_modifica?.id === id) this.annullaModifica();
        })
        .catch(() => {
          this.delete_error = 'Errore durante la dismissione della serra.';
          this.delete_dati_collegati = null;
        });
    },
    riattivaSerra(id) {
      if (this.fork_context) return;
      const serra = (this.$store.getters.getSerre || []).find(s => String(s.id) === String(id));
      if (!serra) return;
      this.$store.dispatch('updateSerra', { id, payload: { ...serra, dismessa: false } })
        .catch(() => {
          this.delete_error = 'Errore durante la riattivazione della serra.';
        });
    },
    eliminaSerra(serra) {
      this.deleting_id = serra.id;
      this.delete_error = "";
      this.delete_dati_collegati = null;
      this.delete_target_id = null;
      const action = this.fork_context
        ? this.$store.dispatch("removeSerraFork", {
            id_layout: this.fork_context.id,
            id_pos: serra._fork_pos_id,
            id_stagione: this.fork_context.id_stagione,
            id_appezzamento: this.fork_context.id_appezzamento,
          }).then(() => {
            if (this.serra_in_modifica?.id === serra.id) this.annullaModifica();
            this.$emit("serre-aggiornate");
          })
        : this.$store.dispatch("deleteSerra", serra.id);
      action
        .catch((error) => {
          const data   = error?.response?.data;
          const status = error?.response?.status;
          if (!this.fork_context && data?.dati_collegati) {
            this.delete_error          = data.message || "Impossibile eliminare la serra.";
            this.delete_dati_collegati = data.dati_collegati;
            this.delete_target_id      = serra.id;
          } else {
            this.delete_error = this.fork_context
              ? "Errore durante la rimozione dal fork."
              : (status === 403
                ? "Non hai i permessi per eliminare questa serra."
                : "Errore durante l'eliminazione. Riprova.");
          }
        })
        .finally(() => { this.deleting_id = null; });
    },
    fileCountPerStagione(id_serra) {
      if (!this.stagione_attiva) return '?';
      return this.$store.getters.getFileStagione(id_serra, this.stagione_attiva.id).length;
    },
    tipoFill(tipo) {
      const map = { ferrovetro: '#E3F2FD', film: '#FFF8E1', ombreggiante: '#EDE7F6', campo_aperto: '#E8F5E9' };
      return map[tipo] || '#ECEFF1';
    },
    tipoStroke(tipo) {
      const map = { ferrovetro: '#1565C0', film: '#E65100', ombreggiante: '#4527A0', campo_aperto: '#2E7D32' };
      return map[tipo] || '#546E7A';
    },
    resetForm() {
      this.serra_form = { nome: "", tipo: null, posizione: null, larghezza: null, lunghezza: null };
      this.file_draft = [];
      this.save_error = "";
      this.save_file_error = "";
      this.serra_in_modifica = null;
      this.$nextTick(() => {
        if (this.$refs.form) this.$refs.form.resetValidation();
      });
    },
    _pushBackState() {
      if (!this.$vuetify.breakpoint.mobile) return;
      history.pushState({ dialog: 'serre' }, '');
      this._backHandler = () => {
        this._backHandler = null;
        this.dialog = false;
        this.resetForm();
      };
      window.addEventListener('popstate', this._backHandler, { once: true });
    },
    _cleanBackState() {
      if (!this._backHandler) return;
      window.removeEventListener('popstate', this._backHandler);
      this._backHandler = null;
      history.back();
    },
    nonNegativeRule(value) {
      if (value === null || value === "" || value === undefined) return true;
      return Number(value) >= 0 || "Valore non valido";
    },
  },
};
</script>

<style scoped>
.lista-col {
  overflow-y: auto;
  max-height: 720px;
}
.file-table {
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  padding: 4px 8px;
  max-height: 280px;
  overflow-y: auto;
}
@media (max-width: 959px) {
  .file-table {
    max-height: 420px;
  }
}
</style>
